// ODS-EWO-033 P3. The shell's command interface: one place to reach anything
// Studio frames, by typing its name. It runs nothing — every command is a
// destination, and the surface it lands on still asks for whatever it governs.
// Deterministic by construction: no model interprets the term, and the same
// term always produces the same list in the same order.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { commandsFor, groupCommands, matchCommands, type Command } from "./commands";
import type { Shell } from "./useShell";

/** Cmd-K on a Mac, Ctrl-K elsewhere, which is where a person reaches for it. */
function isOpenShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
}

export function CommandPalette({
  shell,
  platformAdmin = false,
}: {
  shell: Shell;
  platformAdmin?: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [focused, setFocused] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);

  const commands = useMemo(
    () => commandsFor(shell, { platformAdmin }),
    [shell, platformAdmin],
  );
  const matched = useMemo(() => matchCommands(commands, term), [commands, term]);
  const groups = useMemo(() => groupCommands(matched), [matched]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!isOpenShortcut(event)) return;
      event.preventDefault();
      setOpen((wasOpen) => !wasOpen);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      // ODS-UX-001 §9: the keyboard returns to the control that opened the
      // palette, and only when the palette is what closed — not on first render,
      // where nothing was opened and focus belongs to the page.
      if (wasOpen.current) trigger.current?.focus();
      wasOpen.current = false;
      return;
    }
    wasOpen.current = true;
    setTerm("");
    setFocused(0);
    input.current?.focus();
  }, [open]);

  useEffect(() => setFocused(0), [term]);

  function run(command: Command) {
    setOpen(false);
    navigate(command.to);
  }

  /**
   * The palette is modal, so Tab stays inside it: a keyboard user tabbing out of
   * a dialog and into the page behind it is left operating a surface they cannot
   * see (ODS-UX-001 §9).
   */
  useEffect(() => {
    if (!open || !dialog.current) return;
    const surface: HTMLDivElement = dialog.current;
    function trapTab(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const focusable = surface.querySelectorAll<HTMLElement>(
        "input, button:not([disabled])",
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    surface.addEventListener("keydown", trapTab);
    return () => surface.removeEventListener("keydown", trapTab);
  }, [open, matched.length]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (matched.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocused((i) => (i + 1) % matched.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocused((i) => (i <= 0 ? matched.length - 1 : i - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const command = matched[focused];
      if (command) run(command);
    }
  }

  return (
    <>
      <button
        type="button"
        ref={trigger}
        className="command-palette__trigger ods-btn ods-btn--ghost ods-btn--sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts="Control+K Meta+K"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">⌘</span> Commands
      </button>

      {open && (
        <div className="command-palette">
          {/* Clicking away closes, and the keyboard closes on Escape; the
              overlay itself is not a control, so it carries no role. */}
          <div
            className="command-palette__scrim"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={dialog}
            className="command-palette__dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Studio commands"
          >
            <input
              ref={input}
              type="text"
              className="command-palette__input"
              aria-label="Run a command"
              placeholder="Type a command or a place to go"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {!shell.entitlementsKnown && (
              <p className="command-palette__note" role="status">
                Entitlements are unavailable, so this list may offer an application
                this organization does not hold. Each one still checks on arrival.
              </p>
            )}
            {matched.length === 0 ? (
              <p className="command-palette__note" role="status">
                No command matches that. Commands go to places in Studio; they do
                not act on your behalf.
              </p>
            ) : (
              <div className="command-palette__results">
                {groups.map((group) => (
                  <div key={group.group} className="command-palette__group">
                    <div className="command-palette__group-label">{group.group}</div>
                    {group.commands.map((command) => {
                      const index = matched.indexOf(command);
                      return (
                        <button
                          key={command.id}
                          type="button"
                          className={`command-palette__command ${
                            index === focused ? "command-palette__command--focused" : ""
                          }`}
                          aria-current={index === focused}
                          onClick={() => run(command)}
                          onKeyDown={onKeyDown}
                          onMouseEnter={() => setFocused(index)}
                        >
                          <span aria-hidden="true">{command.icon}</span>
                          <span>{command.label}</span>
                          {command.kind === "offer" && (
                            <span className="command-palette__kind">not held</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
